import { useEffect, useState } from "react";
import { ChatWindow } from "../components/Messaging/ChatWindow";
import { MessagesSidebar } from "../components/Messaging/MessagesSidebar";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";
import { Loader2, ArrowLeft, Users } from "lucide-react";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";

const Messages = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const {
    users,
    selectedUser,
    messages,
    loading,
    error,
    sendMessage,
    setSelectedUser,
    unseenMessages,
    getUsers,
    listingContext // New context for listing information
  } = useChat();

  const [newMessage, setNewMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true); // For mobile toggle

  // Set initial message based on listing context
  useEffect(() => {
    if (listingContext && !messages.length) {
      const contextMessage = `Hi! I'm interested in your listing: "${listingContext.title}". Is this still available for trade?`;
      setNewMessage(contextMessage);
    }
  }, [listingContext, messages.length]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      window.location.href = '/auth?mode=login';
    }
  }, [isAuthenticated, authLoading]);

  // Handle mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowSidebar(true);
      } else if (selectedUser) {
        setShowSidebar(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedUser]);

  // Filter conversations based on search term
  const filteredConversations = users.filter((user) =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser || sendingMessage) {
      return;
    }

    setSendingMessage(true);
    
    const messageData = {
      content: newMessage.trim(),
      type: 'text'
    };

    try {
      const result = await sendMessage(messageData);
      
      if (result && result.success !== false) {
        setNewMessage("");
        // Refresh users list after first message to show new conversation
        if (messages.length === 0) {
          setTimeout(() => getUsers(), 500);
        }
      } else {
        console.error('Failed to send message:', result?.error);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle conversation selection
  const handleSelectConversation = (userId) => {
    const user = users.find(u => u.id === userId || u._id === userId);
    if (user) {
      setSelectedUser(user);
      // On mobile, hide sidebar when selecting conversation
      if (window.innerWidth < 768) {
        setShowSidebar(false);
      }
    }
  };

  // Handle back button (mobile)
  const handleBackToSidebar = () => {
    setShowSidebar(true);
    setSelectedUser(null);
  };

  // Refresh conversations
  const handleRefreshConversations = () => {
    getUsers();
  };

  // Show loading spinner while authenticating
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      {/* Mobile: Full screen chat container */}
      <div className="md:hidden flex-1 flex flex-col">
        {/* Error Alert */}
        {error && (
          <Alert className="m-2 border-red-200 bg-red-50 flex-shrink-0">
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {/* Page Header - Only show when sidebar is visible */}
        {showSidebar && (
          <div className="p-4 bg-white border-b border-gray-200 flex-shrink-0 mt-16">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Messages</h1>
            <p className="text-gray-600 text-sm">
              {users.length > 0 
                ? "Your conversations with other food sharers"
                : "Start conversations by contacting users from their listings"
              }
            </p>
          </div>
        )}

        {/* Mobile Header for Chat - Only show when chat is open */}
        {/* {!showSidebar && selectedUser && (
          <div className="flex items-center p-4 bg-white border-b border-gray-200 flex-shrink-0 mt-16">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToSidebar}
              className="mr-3 p-1"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h2 className="font-semibold text-gray-900">
              {selectedUser.name || selectedUser.firstName || 'User'}
            </h2>
          </div>
        )} */}

        {/* Mobile Chat Interface - Full height container */}
        <div className="flex-1 bg-white overflow-hidden">
          {/* Sidebar */}
          {showSidebar && (
            <div className="h-full">
              <MessagesSidebar
                conversations={filteredConversations}
                activeConversation={selectedUser?.id || selectedUser?._id}
                onSelectConversation={handleSelectConversation}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                unseenMessages={unseenMessages}
                loading={loading}
                onRefresh={handleRefreshConversations}
              />
            </div>
          )}

          {/* Chat Window - Mobile version */}
          {!showSidebar && selectedUser && (
            <ChatWindow
              selectedUser={selectedUser}
              messages={messages}
              newMessage={newMessage}
              onMessageChange={setNewMessage}
              onSendMessage={handleSendMessage}
              loading={loading}
              sendingMessage={sendingMessage}
              isMobile={true}
              onBack={handleBackToSidebar}
            />
          )}
        </div>
      </div>

      {/* Desktop: Main content area */}
      <main className="hidden md:flex flex-1 pt-24 pb-16">
        <div className="container px-4 mx-auto h-full">
          {/* Error Alert */}
          {error && (
            <Alert className="mb-6 border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Messages</h1>
            <p className="text-gray-600">
              {users.length > 0 
                ? "Your conversations with other food sharers"
                : "Start conversations by contacting users from their listings"
              }
            </p>
          </div>

          {/* Main Chat Interface */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 h-[calc(100vh-200px)]">
            <div className="grid grid-cols-12 h-full">
              {/* Conversation Sidebar */}
              <div className="col-span-4 h-full">
                <MessagesSidebar
                  conversations={filteredConversations}
                  activeConversation={selectedUser?.id || selectedUser?._id}
                  onSelectConversation={handleSelectConversation}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  unseenMessages={unseenMessages}
                  loading={loading}
                  onRefresh={handleRefreshConversations}
                />
              </div>

              {/* Chat Window */}
              <div className="col-span-8 h-full">
                <ChatWindow
                  selectedUser={selectedUser}
                  messages={messages}
                  newMessage={newMessage}
                  onMessageChange={setNewMessage}
                  onSendMessage={handleSendMessage}
                  loading={loading}
                  sendingMessage={sendingMessage}
                  isMobile={false}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer - Desktop only */}
      <footer className="hidden md:block bg-igbanwo-mint/5 p-8 border-t border-gray-200">
        <div className="container px-4 mx-auto text-center text-sm text-gray-600">
          &copy; {new Date().getFullYear()} Thoreau Consulting (PTY) Ltd. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Messages;